import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  ArrowRight,
  Eye,
  EyeOff,
  LockKeyhole,
  Mail,
  BarChart3,
  CalendarDays,
  CreditCard,
  UsersRound,
  ShieldCheck,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Surface from '../../components/ui/Surface';
import ClassTechLogo from '../../components/brand/ClassTechLogo';
import { loginUser, loginWithGoogle } from '../../services/auth';
import type { LoginForm } from '../../types';
import { getDefaultRoute } from '../../utils/roleUtils';

const getLoginError = (error: unknown) => {
  const code =
    typeof error === 'object' && error && 'code' in error
      ? String(error.code)
      : '';
  const message =
    error instanceof Error
      ? error.message
      : typeof error === 'string'
        ? error
        : '';
  const value = `${code} ${message}`;

  if (
    value.includes('invalid-credential') ||
    value.includes('user-not-found') ||
    value.includes('wrong-password')
  ) {
    return 'البريد الإلكتروني أو كلمة المرور غير صحيحة.';
  }
  if (value.includes('too-many-requests')) {
    return 'تم تجاوز عدد المحاولات. انتظر قليلًا ثم حاول مرة أخرى.';
  }
  if (value.includes('network-request-failed')) {
    return 'تعذر الاتصال بالإنترنت. تحقق من الشبكة ثم حاول مرة أخرى.';
  }
  return message || 'تعذر تسجيل الدخول حاليًا.';
};

const Login: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>();

  const completeLogin = (role?: string) => {
    toast.success('تم تسجيل الدخول بنجاح.');
    navigate(getDefaultRoute(role || 'student'));
  };

  const onSubmit = async (data: LoginForm) => {
    setLoading(true);
    try {
      const result = await loginUser(data);
      if (!result.user) {
        toast.error(getLoginError(result.error));
        return;
      }
      completeLogin(result.user.role);
    } catch (error) {
      toast.error(getLoginError(error));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const result = await loginWithGoogle();
      if (!result.user) {
        toast.error(getLoginError(result.error));
        return;
      }
      completeLogin(result.user.role);
    } catch (error) {
      toast.error(getLoginError(error));
    } finally {
      setLoading(false);
    }
  };

  const platformHighlights = [
    { icon: UsersRound, label: 'إدارة الطلاب' },
    { icon: CalendarDays, label: 'الحضور والحصص' },
    { icon: CreditCard, label: 'المدفوعات' },
    { icon: BarChart3, label: 'التقارير' },
  ];

  return (
    <main
      className='grid min-h-screen bg-brand-background dark:bg-secondary-950 lg:grid-cols-[minmax(0,1fr)_minmax(28rem,0.85fr)]'
    >
      <section className='relative hidden overflow-hidden bg-sidebar p-12 text-white lg:flex lg:flex-col lg:justify-between'>
        <Link to='/home' className='relative flex items-center gap-3'>
          <ClassTechLogo variant='white' size='lg' showTagline />
        </Link>
        <div className='relative max-w-xl'>
          <span className='inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/10 px-4 py-2 text-sm text-primary-100'>
            <ShieldCheck className='h-4 w-4' />
            منصة تعليمية وإدارية متكاملة
          </span>
          <h1 className='mt-6 text-4xl font-bold leading-tight'>
            كل بيانات الطلاب والحصص والمدفوعات في مساحة واحدة آمنة.
          </h1>
          <p className='mt-4 max-w-lg leading-8 text-secondary-300'>
            يستخدم المدرس والطالب وولي الأمر والموظف نفس نقطة الدخول، ثم يوجهك
            Class Tech تلقائيًا إلى لوحة تناسب صلاحياتك.
          </p>
          <div className='mt-8 grid grid-cols-2 gap-3'>
            {platformHighlights.map(item => {
              const Icon = item.icon;
              return (
                <div
                  key={item.label}
                  className='rounded-lg border border-white/10 bg-white/10 p-4'
                >
                  <Icon className='h-5 w-5 text-brand-cyan' />
                  <p className='mt-3 text-sm font-semibold text-white'>
                    {item.label}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
        <p className='relative text-sm text-secondary-400'>
          حسابات الموظفين تُنشأ بدعوة من إدارة الموارد البشرية فقط.
        </p>
      </section>

      <section className='flex min-h-screen items-center justify-center p-4 sm:p-8'>
        <div className='w-full max-w-md'>
          <div className='mb-8 flex items-center justify-between lg:hidden'>
            <Link to='/home' className='flex items-center gap-2 font-bold'>
              <ClassTechLogo size='sm' />
            </Link>
            <Link
              to='/home'
              className='inline-flex items-center gap-2 text-sm font-semibold text-secondary-600 hover:text-primary-600 dark:text-secondary-300'
            >
              <ArrowRight className='h-4 w-4' />
              العودة
            </Link>
          </div>

          <div className='mb-6'>
            <p className='text-sm font-semibold text-primary-600'>
              أهلًا بعودتك
            </p>
            <h2 className='mt-2 text-3xl font-bold text-secondary-950 dark:text-white'>
              تسجيل الدخول
            </h2>
            <p className='mt-2 text-sm leading-6 text-secondary-500'>
              استخدم بريدك الإلكتروني وكلمة المرور للوصول إلى لوحة التحكم.
              إذا كنت موظفًا جديدًا، استخدم رابط تهيئة كلمة المرور المرسل إلى بريدك.
            </p>
          </div>

          <Surface padding='lg'>
            <form onSubmit={handleSubmit(onSubmit)} className='space-y-5'>
              <div className='relative'>
                <Input
                  label='البريد الإلكتروني'
                  type='email'
                  autoComplete='email'
                  placeholder='name@example.com'
                  error={errors.email?.message}
                  className='pr-10'
                  {...register('email', {
                    required: 'البريد الإلكتروني مطلوب',
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: 'أدخل بريدًا إلكترونيًا صحيحًا',
                    },
                  })}
                />
                <Mail
                  className='pointer-events-none absolute right-3 top-[2.65rem] h-4 w-4 text-secondary-400'
                  aria-hidden='true'
                />
              </div>

              <div className='relative'>
                <Input
                  label='كلمة المرور'
                  type={showPassword ? 'text' : 'password'}
                  autoComplete='current-password'
                  placeholder='أدخل كلمة المرور'
                  error={errors.password?.message}
                  className='px-10'
                  {...register('password', {
                    required: 'كلمة المرور مطلوبة',
                    minLength: {
                      value: 6,
                      message: 'كلمة المرور يجب ألا تقل عن 6 أحرف',
                    },
                  })}
                />
                <LockKeyhole
                  className='pointer-events-none absolute right-3 top-[2.65rem] h-4 w-4 text-secondary-400'
                  aria-hidden='true'
                />
                <button
                  type='button'
                  aria-label={
                    showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'
                  }
                  aria-pressed={showPassword}
                  onClick={() => setShowPassword(value => !value)}
                  className='absolute left-2 top-[2.25rem] flex h-9 w-9 items-center justify-center rounded-lg text-secondary-500 hover:bg-secondary-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 dark:hover:bg-secondary-800'
                >
                  {showPassword ? (
                    <EyeOff className='h-4 w-4' />
                  ) : (
                    <Eye className='h-4 w-4' />
                  )}
                </button>
              </div>

              <div className='flex items-center justify-between gap-4'>
                <label className='flex cursor-pointer items-center gap-2 text-sm text-secondary-600 dark:text-secondary-300'>
                  <input
                    type='checkbox'
                    className='h-4 w-4 rounded border-secondary-300 text-primary-600 focus:ring-primary-500'
                    {...register('rememberMe')}
                  />
                  تذكرني
                </label>
                <Link
                  to='/forgot-password'
                  className='text-sm font-semibold text-primary-600 hover:text-primary-700'
                >
                  نسيت كلمة المرور؟
                </Link>
              </div>

              <Button type='submit' loading={loading} size='lg' className='w-full'>
                تسجيل الدخول
              </Button>

              <div className='flex items-center gap-3 text-xs text-secondary-400'>
                <span className='h-px flex-1 bg-secondary-200 dark:bg-secondary-800' />
                أو
                <span className='h-px flex-1 bg-secondary-200 dark:bg-secondary-800' />
              </div>

              <Button
                type='button'
                variant='secondary'
                size='lg'
                disabled={loading}
                onClick={() => void handleGoogleLogin()}
                className='w-full'
                leadingIcon={
                  <span className='font-bold text-primary-600' aria-hidden='true'>
                    G
                  </span>
                }
              >
                المتابعة باستخدام Google
              </Button>
            </form>
          </Surface>

          <div className='mt-6 space-y-2 text-center text-sm text-secondary-600 dark:text-secondary-300'>
            <p>
              ليس لديك حساب؟{' '}
              <Link to='/register' className='font-bold text-primary-600'>
                إنشاء حساب جديد
              </Link>
            </p>
            <p>
              مدرس جديد؟{' '}
              <Link
                to='/register-teacher'
                className='font-bold text-primary-600'
              >
                التسجيل كمدرس
              </Link>
            </p>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Login;
